import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../../../../components/Badge';
import { Button } from '../../../../components/Button';
import { Input } from '../../../../components/forms/Input';
import { Select } from '../../../../components/forms/Select';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../contexts/useToast';
import { useBookingMutations } from '../hooks';
import { Booking, Resource, SchedulingApiError, SchedulingProfile, Service } from '../types';
import { formatDate, formatMinutes, fromZoned, timeToMinutes, toZoned } from './calendarTime';

export interface CreateDraft {
  date: string;
  minutes: number;
  resourceId: string | null;
}

interface CreateProps {
  profile: SchedulingProfile;
  draft: CreateDraft | null;
  services: Service[];
  resources: Resource[];
  onClose: () => void;
}

interface FormState {
  serviceId: string;
  variantId: string;
  resourceId: string;
  date: string;
  time: string;
  name: string;
  phone: string;
  email: string;
}

// The shared Select also supports multi-select; here only single values occur.
const single = (value: string | string[]): string =>
  Array.isArray(value) ? (value[0] ?? '') : value;

const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

export const CreateBookingDialog: React.FC<CreateProps> = ({
  profile,
  draft,
  services,
  resources,
  onClose,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { create } = useBookingMutations(profile.id);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const activeServices = useMemo(() => services.filter(service => service.is_active), [services]);
  const activeResources = useMemo(
    () => resources.filter(resource => resource.is_active),
    [resources]
  );

  const initialForm = (): FormState => ({
    serviceId: activeServices[0]?.id ?? '',
    variantId: '',
    resourceId: draft?.resourceId ?? '',
    date: draft?.date ?? '',
    time: formatMinutes(draft?.minutes ?? 0),
    name: '',
    phone: '',
    email: '',
  });

  // The dialog is mounted per draft, so the form restarts for every new click.
  const [form, setForm] = useState<FormState>(initialForm);
  const [lastDraft, setLastDraft] = useState(draft);
  if (draft !== lastDraft) {
    setLastDraft(draft);
    setForm(initialForm());
    setFieldErrors({});
    setFormError(null);
  }

  const service = activeServices.find(candidate => candidate.id === form.serviceId);
  const variant = service?.variants.find(candidate => candidate.id === form.variantId);
  // An empty restriction list means the service is offered by every resource.
  const eligible = activeResources.filter(
    resource =>
      !service || service.resources.length === 0 || service.resources.includes(resource.id)
  );
  const duration = variant?.duration_minutes ?? service?.duration_minutes ?? 0;
  // A pre-selected lane may not offer the first service; show nothing instead of its raw id.
  const resourceId = eligible.some(resource => resource.id === form.resourceId)
    ? form.resourceId
    : '';
  const startMinutes = timeToMinutes(form.time);

  const update = (patch: Partial<FormState>) => setForm(current => ({ ...current, ...patch }));

  const handleSubmit = async () => {
    if (!service) return;
    setFieldErrors({});
    setFormError(null);
    const start = fromZoned(form.date, startMinutes, profile.timezone);
    if (start <= new Date()) {
      setFormError(t('schedulingAdmin.calendar.errors.startInPast'));
      return;
    }
    try {
      await create.mutateAsync({
        service: service.id,
        variant: form.variantId || null,
        resource: resourceId,
        start_datetime: start.toISOString(),
        customer_name: form.name.trim(),
        customer_phone: form.phone.trim(),
        ...(form.email.trim() ? { customer_email: form.email.trim() } : {}),
      });
      showToast({ type: 'success', message: t('schedulingAdmin.calendar.created') });
      onClose();
    } catch (error) {
      if (error instanceof SchedulingApiError) {
        if (error.status === 409) {
          setFormError(t('schedulingAdmin.calendar.errors.occupied'));
        } else if (error.code === 'start_in_past') {
          setFormError(t('schedulingAdmin.calendar.errors.startInPast'));
        } else {
          setFieldErrors(error.fieldErrors);
          setFormError(error.nonFieldErrors[0] ?? null);
        }
      } else {
        setFormError(t('schedulingAdmin.errors.generic'));
      }
    }
  };

  const canSubmit =
    !!service &&
    !!resourceId &&
    !!form.date &&
    !!form.time &&
    !!form.name.trim() &&
    !!form.phone.trim();

  return (
    <Modal
      isOpen={!!draft}
      onClose={onClose}
      title={t('schedulingAdmin.calendar.createTitle')}
      size="md"
    >
      <div className="space-y-4">
        <div>
          <label className={labelClass}>{t('schedulingAdmin.calendar.service')}</label>
          <Select
            options={activeServices.map(item => ({ value: item.id, label: item.name }))}
            value={form.serviceId}
            onChange={selection => {
              const value = single(selection);
              const next = activeServices.find(item => item.id === value);
              const stillEligible =
                !next || next.resources.length === 0 || next.resources.includes(form.resourceId);
              update({
                serviceId: value,
                variantId: '',
                resourceId: stillEligible ? form.resourceId : '',
              });
            }}
          />
          {fieldErrors.service && (
            <p className="mt-1 text-sm text-red-600">{fieldErrors.service}</p>
          )}
        </div>

        {service && service.variants.length > 0 && (
          <div>
            <label className={labelClass}>{t('schedulingAdmin.calendar.variant')}</label>
            <Select
              options={[
                { value: '', label: t('schedulingAdmin.calendar.noVariant') },
                ...service.variants.map(item => ({
                  value: item.id,
                  label: `${item.name} (${item.duration_minutes} min)`,
                })),
              ]}
              value={form.variantId}
              onChange={value => update({ variantId: single(value) })}
            />
            {fieldErrors.variant && (
              <p className="mt-1 text-sm text-red-600">{fieldErrors.variant}</p>
            )}
          </div>
        )}

        <div>
          <label className={labelClass}>{t('schedulingAdmin.calendar.resource')}</label>
          <Select
            options={eligible.map(item => ({ value: item.id, label: item.name }))}
            value={resourceId}
            onChange={value => update({ resourceId: single(value) })}
            placeholder={t('schedulingAdmin.calendar.resourcePlaceholder')}
          />
          {fieldErrors.resource && (
            <p className="mt-1 text-sm text-red-600">{fieldErrors.resource}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            type="date"
            label={t('schedulingAdmin.calendar.date')}
            value={form.date}
            onChange={e => update({ date: e.target.value })}
            errorText={fieldErrors.start_datetime}
            state={fieldErrors.start_datetime ? 'error' : 'default'}
            fullWidth
          />
          <Input
            type="time"
            step={profile.slot_granularity_minutes * 60}
            label={t('schedulingAdmin.calendar.time')}
            value={form.time}
            onChange={e => update({ time: e.target.value })}
            helperText={
              duration > 0
                ? t('schedulingAdmin.calendar.endsAt', {
                    time: formatMinutes((startMinutes + duration) % 1440),
                  })
                : undefined
            }
            fullWidth
          />
        </div>

        <Input
          label={t('schedulingAdmin.calendar.customerName')}
          value={form.name}
          onChange={e => update({ name: e.target.value })}
          errorText={fieldErrors.customer_name}
          state={fieldErrors.customer_name ? 'error' : 'default'}
          fullWidth
          required
        />
        <Input
          type="tel"
          label={t('schedulingAdmin.calendar.customerPhone')}
          value={form.phone}
          onChange={e => update({ phone: e.target.value })}
          helperText={t('schedulingAdmin.calendar.phoneHelp')}
          errorText={fieldErrors.customer_phone}
          state={fieldErrors.customer_phone ? 'error' : 'default'}
          fullWidth
          required
        />
        <Input
          type="email"
          label={t('schedulingAdmin.calendar.customerEmail')}
          value={form.email}
          onChange={e => update({ email: e.target.value })}
          errorText={fieldErrors.customer_email}
          state={fieldErrors.customer_email ? 'error' : 'default'}
          fullWidth
        />

        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('schedulingAdmin.calendar.manualHint')}
        </p>
        {formError && <p className="text-sm text-red-600">{formError}</p>}

        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || create.isPending}>
            {t('schedulingAdmin.calendar.book')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

interface DetailsProps {
  profile: SchedulingProfile;
  booking: Booking | null;
  // Why the appointment has to be rescheduled, if it does.
  warning?: string;
  onClose: () => void;
}

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="grid grid-cols-3 gap-2 text-sm">
    <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
    <dd className="col-span-2 text-gray-900 dark:text-white break-words">{children}</dd>
  </div>
);

export const BookingDetailsDialog: React.FC<DetailsProps> = ({
  profile,
  booking,
  warning,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const { cancel, markNoShow } = useBookingMutations(profile.id);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setConfirming(false);
    setError(null);
    onClose();
  };

  const handleCancel = async () => {
    if (!booking) return;
    setError(null);
    try {
      await cancel.mutateAsync(booking.id);
      showToast({ type: 'success', message: t('schedulingAdmin.calendar.cancelled') });
      close();
    } catch {
      setError(t('schedulingAdmin.errors.generic'));
    }
  };

  const handleNoShow = async () => {
    if (!booking) return;
    setError(null);
    try {
      await markNoShow.mutateAsync(booking.id);
      showToast({ type: 'success', message: t('schedulingAdmin.calendar.noShowDone') });
      close();
    } catch {
      setError(t('schedulingAdmin.errors.generic'));
    }
  };

  const start = booking ? toZoned(booking.start_datetime, profile.timezone) : null;
  const end = booking ? toZoned(booking.end_datetime, profile.timezone) : null;
  const confirmed = booking?.status === 'confirmed';
  const started = booking ? new Date(booking.start_datetime).getTime() <= Date.now() : false;
  // Inactive calendars are read-only, so upcoming bookings can be neither moved nor cancelled.
  const changeable = confirmed && !started && profile.is_active;

  return (
    <Modal
      isOpen={!!booking}
      onClose={close}
      title={t('schedulingAdmin.calendar.detailsTitle')}
      size="md"
    >
      {booking && start && end && (
        <div className="space-y-4">
          <dl className="space-y-2">
            <Row label={t('schedulingAdmin.calendar.service')}>
              {booking.service_name}
              {booking.variant_name && ` · ${booking.variant_name}`}
            </Row>
            <Row label={t('schedulingAdmin.calendar.resource')}>{booking.resource_name}</Row>
            <Row label={t('schedulingAdmin.calendar.when')}>
              {formatDate(start.date, i18n.language, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
              , {formatMinutes(start.minutes)}–{formatMinutes(end.minutes)}
            </Row>
            <Row label={t('schedulingAdmin.calendar.customerName')}>{booking.customer_name}</Row>
            <Row label={t('schedulingAdmin.calendar.customerPhone')}>{booking.customer_phone}</Row>
            {booking.customer_email && (
              <Row label={t('schedulingAdmin.calendar.customerEmail')}>
                {booking.customer_email}
              </Row>
            )}
            <Row label={t('schedulingAdmin.calendar.source')}>
              <Badge variant="info" size="sm">
                {t(`schedulingAdmin.calendar.sources.${booking.source}`)}
              </Badge>
            </Row>
            {booking.status !== 'confirmed' && (
              <Row label={t('schedulingAdmin.calendar.status')}>
                <Badge variant="warning" size="sm">
                  {t(`schedulingAdmin.calendar.statuses.${booking.status}`)}
                </Badge>
              </Row>
            )}
          </dl>

          {warning && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-200">
              <span className="material-icons text-lg leading-none text-red-500">warning</span>
              <span>{warning}</span>
            </div>
          )}

          {changeable && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('schedulingAdmin.calendar.moveHint')}
            </p>
          )}
          {confirmed && !started && !profile.is_active && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('schedulingAdmin.calendar.errors.profileInactive')}
            </p>
          )}
          {confirmed && started && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('schedulingAdmin.calendar.noShowHint')}
            </p>
          )}

          {confirming && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-3 dark:border-red-900 dark:bg-red-900/20">
              <p className="text-sm text-red-700 dark:text-red-300">
                {t('schedulingAdmin.calendar.cancelText', { name: booking.customer_name })}
                {profile.send_confirmation_sms &&
                  ` ${t('schedulingAdmin.calendar.cancelCustomerInformed')}`}
              </p>
              <div className="flex justify-end gap-3">
                <Button variant="secondary" onClick={() => setConfirming(false)}>
                  {t('schedulingAdmin.calendar.keep')}
                </Button>
                <Button variant="danger" onClick={handleCancel} disabled={cancel.isPending}>
                  {t('schedulingAdmin.calendar.cancelConfirm')}
                </Button>
              </div>
            </div>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3">
            {confirmed && started && !confirming && (
              <Button variant="secondary" onClick={handleNoShow} disabled={markNoShow.isPending}>
                {t('schedulingAdmin.calendar.noShow')}
              </Button>
            )}
            {changeable && !confirming && (
              <Button variant="danger" onClick={() => setConfirming(true)}>
                {t('schedulingAdmin.calendar.cancelBooking')}
              </Button>
            )}
            <Button variant="secondary" onClick={close}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
